import { Check } from "lucide-react";
import { Link } from "react-router-dom";
import { blogPostPath } from "@/blog/routes";
import type { BlogPostSummary } from "@/blog/types";

type Props = {
  posts: readonly BlogPostSummary[];
  read: ReadonlySet<string>;
  tone?: "navy" | "light";
  /** Show each article's summary under its title (route pages). */
  detailed?: boolean;
  headingLevel?: "h2" | "h3" | "h4";
};

/**
 * A route as an ordered path. Articles the visitor finished are ticked and
 * the first unread one is lit as the next step.
 */
export function RouteSteps({ posts, read, tone = "light", detailed = false, headingLevel: Heading = "h3" }: Props) {
  const navy = tone === "navy";
  const nextIndex = posts.findIndex((post) => !read.has(post.slug));
  const started = posts.some((post) => read.has(post.slug));

  return (
    <ol className="relative">
      {posts.map((post, i) => {
        const done = read.has(post.slug);
        const next = i === nextIndex;
        const note = done ? "Leído" : next ? (started ? "Siguiente paso recomendado" : "Empezá por acá") : null;
        return (
          <li
            key={post.slug}
            className={`group/step relative grid grid-cols-[2.75rem_1fr] items-start gap-x-4 gap-y-1 rounded-2xl px-3 py-4 transition-colors duration-300 sm:grid-cols-[2.75rem_1fr_auto] sm:gap-x-5 sm:px-4 ${
              next
                ? navy
                  ? "bg-white/[0.06] ring-1 ring-brand-secondary/60"
                  : "bg-white shadow-lit ring-1 ring-brand-secondary/50"
                : navy
                  ? "hover:bg-white/[0.04]"
                  : "hover:bg-white/70"
            }`}
          >
            {/* Thread to the next step */}
            {i < posts.length - 1 ? (
              <span
                aria-hidden="true"
                className={`absolute left-[2.1rem] top-[3.4rem] h-[calc(100%-2.4rem)] w-0.5 rounded-full sm:left-[2.35rem] ${
                  done ? "bg-brand-secondary" : navy ? "bg-white/12" : "bg-brand-primary/10"
                }`}
              />
            ) : null}
            <span
              aria-hidden="true"
              className={`relative z-10 grid size-10 place-items-center rounded-full border-2 font-display text-base font-semibold [font-variant-numeric:lining-nums_tabular-nums] transition-[background-color,border-color,color,scale] duration-300 motion-safe:group-hover/step:scale-105 ${
                next
                  ? "border-brand-secondary bg-brand-secondary text-brand-primary shadow-[0_0_0_7px_rgb(114_181_177/0.2)]"
                  : done
                    ? navy
                      ? "border-brand-secondary text-teal-light"
                      : "border-brand-secondary bg-teal-soft text-teal-strong"
                    : navy
                      ? "border-white/20 text-on-navy-muted group-hover/step:border-brand-secondary/70"
                      : "border-brand-primary/15 bg-white text-brand-primary group-hover/step:border-brand-secondary"
              }`}
            >
              {done ? <Check className="size-4" strokeWidth={3} /> : (post.routeStep ?? i + 1)}
            </span>
            <div className="min-w-0 pt-1.5">
              <Heading className={`font-display text-lg font-semibold leading-snug sm:text-xl ${navy ? "text-white" : "text-brand-primary"}`}>
                <Link
                  to={blogPostPath(post.slug)}
                  className={`rounded-md transition-colors duration-300 after:absolute after:inset-0 after:content-[''] ${
                    navy ? "group-hover/step:text-teal-light" : "group-hover/step:text-teal-strong"
                  }`}
                >
                  {post.title}
                </Link>
              </Heading>
              {detailed ? (
                <p className={`mt-2 leading-relaxed ${navy ? "text-on-navy-muted" : "text-ink-soft"}`}>{post.excerpt}</p>
              ) : null}
              {note ? (
                <p className={`mt-1 text-sm font-medium ${next ? (navy ? "text-teal-light" : "text-teal-strong") : navy ? "text-on-navy-muted" : "text-ink-muted"}`}>
                  {note}
                </p>
              ) : null}
            </div>
            <span className={`col-start-2 text-sm sm:col-start-3 sm:pt-2 ${navy ? "text-on-navy-muted" : "text-ink-muted"}`}>
              {post.readingMinutes} min
            </span>
          </li>
        );
      })}
    </ol>
  );
}
